import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceServices } from '../services/service.service';
import toast from 'react-hot-toast';

export const useToggleServiceStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, payload }) => serviceServices.updateServiceStatus({ serviceId, payload }),

    // Optimistic update for isActive toggle
    onMutate: async ({ serviceId, payload }) => {
      // Only do optimistic update for isActive changes
      if (payload.isActive === undefined) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['services'] });

      // Snapshot previous values
      const previousData = queryClient.getQueriesData({ queryKey: ['services'] });

      // Optimistically update all matching queries
      queryClient.setQueriesData({ queryKey: ['services'] }, (old) => {
        if (!old) return old;

        // Handle the actual API response structure: { status, message, pagination, data: [] }
        const services = old.data || [];

        // Ensure services is an array before mapping
        if (!Array.isArray(services)) {
          console.error('Expected services to be an array, got:', services);
          return old;
        }

        const updatedServices = services.map((service) =>
          service._id === serviceId ? { ...service, isActive: payload.isActive } : service
        );

        return {
          ...old,
          data: updatedServices,
        };
      });

      return { previousData };
    },

    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(error?.response?.data?.message || 'Failed to update service status');
    },

    onSuccess: (data) => {
      const message = data?.message || 'Service status updated successfully';
      toast.success(message);
    },

    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
};
